import {
    Li,
    ModalDialog,
    ModalDialogBody,
    ModalDialogCloseButton,
    ModalDialogContent,
    ModalDialogHeader,
    ModalDialogNav,
    ModalDialogTitle,
    Typography,
    Ul,
} from '@vp/swan';
import { format, parseISO } from 'date-fns';
import { Currency, bugtracker, price } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { AgreementType } from './ProductCard';

function formatDate(date?: string) {
    if (!date) {
        return '';
    }

    const parsedDate = parseISO(date);
    return format(parsedDate, 'MMMM d, yyyy');
}

const formatDay = (days: number): { dayTotal: number; dayCopy: string } => {
    return {
        dayTotal: days,
        dayCopy:
            days === 1
                ? __('day', {
                      _context:
                          'Singular form of day displayed to freelance designers in the design service statement of work near a numerical value of 1, e.g. 1 day or 1 business day',
                  })
                : __('days', {
                      _context:
                          'Pluralised form of day displayed to freelance designers in the design service statement of work near a numerical value greater than 1, e.g. 7 days or 7 business days',
                  }),
    };
};

// This is a static equivalent of https://gitlab.com/vistaprint-org/99designs/frontend/-/blob/master/graph/graph/job.go#L58-65
export interface Agreement {
    revisions: number;
    turnaround: number;
    deliverables: number;
    deliverableType: string;
    payDeadline: number;
    requestDeadline: number;
}

// Warning: This statement of work is the read-only equivalent of the one initially shown to freelance designers when applying for a job in Matched Projects.
// Any changes here also need to be reflected in that statement of work.
//
// https://gitlab.com/vistaprint-org/99designs/frontend/-/blob/master/libs/feature-browse-projects/src/lib/JobApply/Section/JobApplication/Steps/StatementOfWork.tsx
//
// These disparate statements of work components should be extracted into a central source at some point in the near future.
// The agreement criteria templated in the statement of work should also be hoisted into the product model and set in Contentful or the PIM.
// Finally, all redundant static types and props, such as Agreement and AgreementType should be deleted.
export const SOWModal = ({
    isOpen,
    setIsOpen,
    requestId,
    agreement,
    matchedDate,
    earnings,
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    requestId: string;
    agreement: Agreement;
    matchedDate?: string;
    earnings?: number;
}) => {
    if (!matchedDate) {
        bugtracker.notify(
            new Error(`No matched date found shown in designer SOW for job: ${requestId}`)
        );
    }

    if (!earnings) {
        bugtracker.notify(new Error(`No earnings shown in designer SOW for job: ${requestId}`));
    }

    return (
        <ModalDialog
            variant="panel-right"
            bodyWidth="capped"
            isOpen={isOpen}
            onRequestDismiss={() => {
                setIsOpen(false);
            }}
        >
            <ModalDialogContent aria-labelledby="modal dialog panel">
                <ModalDialogNav>
                    <ModalDialogCloseButton accessibleText="Close" />
                </ModalDialogNav>
                <ModalDialogHeader>
                    <ModalDialogTitle>{__('Statement of Work')}</ModalDialogTitle>
                </ModalDialogHeader>
                <ModalDialogBody>
                    <Typography mt={6}>
                        {__(
                            'This Statement of Work (SOW) dated as of {{matchedDate}}, is entered into by and between Vista and You and is subject to the terms and conditions of the Master Design Service Agreement in all respects.',
                            {
                                matchedDate:
                                    formatDate(matchedDate) || __('the project start date'),
                                _context: `This Statement of Work is shown to designers after they've been accepted for the project as a reminder of what they've agreed to.`,
                            }
                        )}
                    </Typography>
                    <Typography mt={5}>
                        {__(
                            'This SOW applies to the design project that is based upon the following Design Request from VistaPrint customer: {{requestId}}.',
                            { requestId }
                        )}
                    </Typography>
                    <Typography mt={6} fontSkin={'body-standard-bold'}>
                        {__('Design Services')}
                    </Typography>
                    <Typography mt={5}>
                        {__('You shall provide Vista with the following inclusions:')}
                    </Typography>
                    <Ul mt={5}>
                        <Li>
                            {__(
                                'At least one {{deliverables}} {{deliverableType}} based on and responsive to the requirements and specifications of the Design Request.',
                                {
                                    deliverables: agreement.deliverables,
                                    deliverableType: agreement.deliverableType,
                                    _context: `Displayed to freelance designers in the design service statement of work, that instructs them on the type and quantity of the deliverables they must produce for a customer`,
                                }
                            )}
                        </Li>
                        <Li>
                            {__(
                                'Up to {{revisions}} rounds of revisions based on feedback provided by the VistaPrint customer via the VistaPrint Site following the provision of the initial design concept.',
                                {
                                    revisions: agreement.revisions,
                                    _context: `Displayed to freelance designers in the design service statement of work that tells them the revision limit`,
                                }
                            )}
                        </Li>
                    </Ul>
                    <Typography mt={6} fontSkin={'body-standard-bold'}>
                        {__('Deliverables')}
                    </Typography>
                    <Typography mt={5}>
                        {__(
                            'You agree to deliver the following items as part of the Design Services:'
                        )}
                    </Typography>
                    <Ul mt={5}>
                        <Li>
                            {__(
                                'You must upload and deliver the initial design as well as any revised designs following the VistaPrint customer’s submission of the feedback on the initial design concept or revision through the Design Studio via the VistaPrint Site that is provided to you to work on the design project.'
                            )}
                        </Li>
                        <Li>
                            {__(
                                'You must provide the initial design as well as any revised designs to Vista in high-resolution file format that are suitable for commercial printing (atleast 300dpi in CMYK color mode). Acceptable file formats include AI, PDF, SVG, EPS vector file formats. You must ensure all files are print-ready, meet Vista’s print and quality standards and specifications, and are fully editable to allow for future adaptation or use by Vista.'
                            )}
                        </Li>
                    </Ul>
                    <Typography mt={6} fontSkin={'body-standard-bold'}>
                        {__('Timeline and turnaround times')}
                    </Typography>
                    <Ul mt={5}>
                        <Li>
                            {__(
                                'You must deliver your initial design concept within {{dayTotal}} business {{dayCopy}} following the SOW Effective Date and deliver revisions within {{dayTotal}} business {{dayCopy}} following the VistaPrint customer’s submission of feedback via the VistaPrint Site on the initial design concept or revision, as the case may be. For purposes of this provision, “business days” excludes weekends.',
                                {
                                    ...formatDay(agreement.turnaround),
                                    _context:
                                        'Displayed to freelance designers in the design service statement of work that tells them the number of days they have to complete the work for the customer',
                                }
                            )}
                        </Li>
                        <Li>
                            {__(
                                'The design project will automatically close {{dayTotal}} {{dayCopy}} after the SOW effective date and the VistaPrint customer will no longer be able to request revisions.',
                                {
                                    ...formatDay(agreement.requestDeadline),
                                    _context:
                                        'Displayed to freelance designers in the design service statement of work that tells them the maximum deadline for a project',
                                }
                            )}
                        </Li>
                    </Ul>
                    <Typography mt={6} fontSkin={'body-standard-bold'}>
                        {__('Earnings and Payment')}
                    </Typography>
                    <Ul mt={5}>
                        <Li>
                            {__(
                                `Your earnings for completing this design project will be {{earnings}} which will be paid as Credits into your account, upon full and satisfactory completion of the Design Request which will be determined at Vista’s sole discretion.`,
                                {
                                    earnings: price({
                                        amount: earnings ?? 0,
                                        currency: Currency.USD,
                                        decimalPlaces: 2,
                                    }),
                                }
                            )}
                        </Li>
                        <Li>
                            {__('Payment will be issued upon the earliest of the following:')}
                            <Ul>
                                <Li>
                                    {__(
                                        'The customer approves the initial design concept or any revised designs OR'
                                    )}
                                </Li>
                                <Li>
                                    {__(
                                        '{{dayTotal}} {{dayCopy}} after all revisions have been completed OR',
                                        {
                                            ...formatDay(agreement.payDeadline),
                                            _context:
                                                'Displayed to freelance designers in the design service statement of work that informs them of the conditions for them to be paid',
                                        }
                                    )}
                                </Li>
                                <Li>
                                    {__(
                                        '{{dayTotal}} {{dayCopy}} after you deliver the initial design concept or any revised designs and there is no activity from the VistaPrint customer',
                                        {
                                            ...formatDay(agreement.payDeadline),
                                            _context:
                                                'Displayed to freelance designers in the design service statement of work that informs them of the conditions for them to be paid',
                                        }
                                    )}
                                    <Ul>
                                        <Li>
                                            {__(
                                                '“Activity” refers to any action by the VistaPrint customer related to this design project, including a request for revision. If the VistaPrint customer requests a revision, the {{dayTotal}} day payment timeframe is cancelled and will restart when you deliver the revised design in accordance with the revision request.',
                                                {
                                                    dayTotal: agreement.payDeadline,
                                                    _context:
                                                        'Displayed to freelance designers in the design service statement of work that informs them of the conditions for them to be paid',
                                                }
                                            )}
                                        </Li>
                                    </Ul>
                                </Li>
                            </Ul>
                        </Li>
                    </Ul>
                    <Typography mt={6} fontSkin={'body-standard-bold'}>
                        {__('Additional Terms')}
                    </Typography>
                    <Typography mt={5} mb={8}>
                        {__(
                            'You are prohibited from communicating or otherwise dealing with the VistaPrint customer outside the VistaPrint Site.'
                        )}
                    </Typography>
                </ModalDialogBody>
            </ModalDialogContent>
        </ModalDialog>
    );
};
